// ============================================================================
// DUPLICATED LOGIC: also implemented in backend/app/public/spread.py.
// Change both and update the parity fixtures in shared/vectors/spread.json.
// Why: the public API resolves spread rates on the server (ADR 0003 amendment).
// CI runs the same fixtures in frontend/src/lib/spread.vectors.test.ts and
// backend/worker_tests/test_spread_vectors.py. A change to one side alone fails CI.
// ============================================================================
// What that file establishes, measured rather than assumed zip name
// <Field>_<Products>_<Farm>-Recs_<Grower>_<epochSeconds>.
import { postJsonForFile } from "../api/httpGateway";

/* * Strip to what the reference filename allows: their tokens carry no spaces or punctuation. */
export function fileToken(s: string): string {
  return (s || "").replace(/[^A-Za-z0-9]+/g, "");
}

/*
 *
 * Reserving the digits first gives `Muriateo60`: uglier, and still says which product at which
 * strength.
 */
export function productColumn(label: string, efficiencyPct: number): string {
  const pct = String(Math.round(efficiencyPct));
  return `${fileToken(label).slice(0, Math.max(1, 10 - pct.length))}${pct}`;
}

/* * One field's contribution: its own folder, its own shapefile triple. */
export interface SpreadFieldExport {
  fieldName: string;
  farmName: string;
  /* * OUR field id — the send lane addresses by id, never by name (both producers stamp it) */
  fieldId?: string;
  /* * the SavedRecSet the rates came from, when a single set answers for them — rides the send history */
  rxId?: string;
  /* * one entry per product column, in column order */
  products: { column: string; rates: (number | null)[] }[];
  /* * per-cell geometry, index-aligned with every product's `rates` */
  geoms: { type?: string; coordinates?: unknown }[];
}

export interface SpreadExportInput {
  fields: SpreadFieldExport[];
  /* * the org / grower the file is for — the last token of the reference filename */
  growerName: string;
  /* * stamped into the zip name and the dbf header; passed in so nothing here reads the clock */
  now: Date;
}

/*
 *
 * A member basename — no extension, shared by that field's three files.
 * `Multi` is the reference's own token for "more than one": it uses it in the PRODUCT slot, and this
 * uses it in the FIELD slot for a multi-field zip by the same logic.
 */
export function spreadBasename(i: {
  fieldName: string;
  farmName: string;
  growerName: string;
  products: { column: string }[];
}): string {
  const products =
    i.products.length === 1 ? fileToken(i.products[0]!.column) : "Multi";
  return `${fileToken(i.fieldName)}_${products}_${fileToken(i.farmName)}-Recs_${fileToken(i.growerName)}`;
}

/*
 *
 * A cell the Rx could not answer for (no modelled soil test under it) is not a cell that needs
 * nothing — writing 0 would instruct a spreader to deliberately skip ground the agronomy never
 * ruled on, and the row would be indistinguishable from a genuine zero.
 */
function epochSeconds(now: Date): number {
  return Math.floor(now.getTime() / 1000);
}

function filenameOf(header: string | null, fallback: string): string {
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header ?? "");
  if (encoded?.[1]) return decodeURIComponent(encoded[1]);
  const plain = /filename="([^"]+)"/i.exec(header ?? "");
  return plain?.[1] || fallback;
}

async function bytesOf(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === "function")
    return new Uint8Array(await blob.arrayBuffer());
  // jsdom's Blob has no arrayBuffer. FileReader is the same bytes.
  const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
  return new Uint8Array(buffer);
}

function requestBody(input: SpreadExportInput) {
  return {
    growerName: input.growerName,
    epochSeconds: epochSeconds(input.now),
    fields: input.fields.map((field) => ({
      fieldName: field.fieldName,
      farmName: field.farmName,
      fieldId: field.fieldId,
      products: field.products,
      geoms: field.geoms,
    })),
  };
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/* * The calendar day on the machine asking for the file, so the server does not shift it. */
export function spreadReportDate(now: Date): string {
  return `${MONTHS[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`;
}

export interface SpreadPdfInput extends SpreadExportInput {
  productLabel: string;
  columnName: string;
}

/* * The report is written on the server from these rates. */
export async function buildSpreadPdf(input: SpreadPdfInput): Promise<{
  name: string;
  data: Uint8Array;
}> {
  const { blob, disposition } = await postJsonForFile("/exports/spread/pdf", {
    ...requestBody(input),
    productLabel: input.productLabel,
    columnName: input.columnName,
    localDate: spreadReportDate(input.now),
  });
  return {
    name: filenameOf(disposition, "spread.pdf"),
    data: await bytesOf(blob),
  };
}

/* * The shapefile is written on the server from these rates. A cell with no rate is omitted there. */
export async function buildSpreadZip(input: SpreadExportInput): Promise<{
  name: string;
  data: Uint8Array;
}> {
  const { blob, disposition } = await postJsonForFile(
    "/exports/spread",
    requestBody(input),
  );
  return {
    name: filenameOf(disposition, "spread.zip"),
    data: await bytesOf(blob),
  };
}

/*
 *
 * THE ONLY DIFFERENCE FROM `buildSpreadZip` IS THE FOLDER, and that is not a detail. Deere's Files
 * documentation is explicit that the shapefile must sit in a directory named **`Rx`**; ours nests
 * `<Farm> - Recs/<Field>/` to match the export supplied as the download template.
 */
/*
 * Deere requires the shapefile in a directory named Rx, one field per zip. A field
 * with nothing to spread is skipped, the same as an empty download folder.
 */
export async function buildJdRxZips(
  input: SpreadExportInput,
): Promise<{ fieldName: string; name: string; data: Uint8Array }[]> {
  const out: { fieldName: string; name: string; data: Uint8Array }[] = [];
  for (const field of input.fields) {
    try {
      const { blob, disposition } = await postJsonForFile(
        "/exports/spread/jd",
        requestBody({ ...input, fields: [field] }),
      );
      out.push({
        fieldName: field.fieldName,
        name: filenameOf(disposition, "Rx.zip"),
        data: await bytesOf(blob),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      if (message.includes("nothing to spread")) continue;
      throw error;
    }
  }
  return out;
}

/*
 *
 * One rule for both is a 2,000× error on a spreader instruction, so the nutrient is a REQUIRED
 * argument rather than something inferred from magnitude. THE ONLY PLACE THIS CONVERSION HAPPENS.
 * Lime Rx are tons of 90% CCE lime. analysis is still a CCE percent. Multiplier = 90 / percent,
 * so product pounds = Rx tons × 2000 × 90 / percent. P and K stay lbs ÷ (percent/100).
 */
export function productLbsPerAc(
  pure: number | null | undefined,
  efficiencyPct: number,
  nutrient: "lime" | "p2o5" | "k2o",
): number | null {
  if (pure == null || !Number.isFinite(pure)) return null;
  const e =
    Number.isFinite(efficiencyPct) && efficiencyPct > 0 ? efficiencyPct : 100;
  if (nutrient === "lime") return Math.round((pure * 2000 * 90) / e);
  return Math.round((pure * 100) / e);
}
